import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star, 
  Trash2, 
  TrendingUp, 
  Search, 
  RefreshCw, 
  ExternalLink,
  Plus,
  Eye,
  Loader2,
  Clock
} from "lucide-react";

export const WatchlistPage = () => {
  const { token } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchWatchlist = useCallback(async (withNav = true) => {
    try {
      const endpoint = withNav ? `${API}/watchlist/live` : `${API}/watchlist`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setWatchlist(response.data.watchlist || []);
      if (response.data.last_updated) {
        setLastUpdated(new Date(response.data.last_updated));
      }
    } catch (error) {
      console.error("Error fetching watchlist:", error);
      toast.error("Failed to fetch watchlist");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  const refreshNAVs = async () => {
    setRefreshing(true);
    await fetchWatchlist(true);
    toast.success("NAVs updated!");
  };

  const searchFunds = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search`, {
        params: { q: searchQuery }
      });
      setSearchResults(response.data.funds || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchFunds();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchFunds]);

  const addToWatchlist = async (fund) => {
    try {
      await axios.post(`${API}/watchlist`, {
        scheme_code: fund.schemeCode || fund.scheme_code,
        scheme_name: fund.schemeName || fund.scheme_name,
        category: fund.category || "Equity"
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Added to watchlist!");
      setSearchQuery("");
      setSearchResults([]);
      fetchWatchlist(true);
    } catch (error) {
      if (error.response?.data?.detail === "Fund already in watchlist") {
        toast.error("Fund already in watchlist");
      } else {
        toast.error("Failed to add to watchlist");
      }
    }
  };

  const removeFromWatchlist = async (schemeCode) => {
    try {
      await axios.delete(`${API}/watchlist/${schemeCode}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Removed from watchlist");
      setWatchlist(watchlist.filter(item => item.scheme_code !== schemeCode));
    } catch (error) {
      toast.error("Failed to remove from watchlist");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  // Sample funds for quick add
  const sampleFunds = [
    { schemeCode: "119551", schemeName: "Aditya Birla Sun Life Frontline Equity Fund", category: "Large Cap" },
    { schemeCode: "120503", schemeName: "HDFC Mid-Cap Opportunities Fund", category: "Mid Cap" },
    { schemeCode: "118989", schemeName: "Axis Bluechip Fund", category: "Large Cap" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="watchlist-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              My <span className="gold-text">Watchlist</span>
            </h1>
            <p className="text-muted-foreground">Track funds you're interested in with live NAV updates</p>
          </div>

          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              className="border-white/10"
              onClick={refreshNAVs}
              disabled={refreshing}
              data-testid="refresh-nav-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh NAVs
            </Button>
          </div>
        </div>

        {/* Search to Add */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Add Funds to Watchlist
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search funds to add..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-secondary/50 border-white/10"
                data-testid="watchlist-search-input"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" />
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                {searchResults.map((fund, index) => (
                  <div
                    key={fund.schemeCode || index}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                    data-testid={`search-result-${index}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{fund.schemeName}</p>
                      <p className="text-xs text-muted-foreground">{fund.category || "Equity"}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addToWatchlist(fund)}
                      data-testid={`add-fund-${index}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Add */}
            {!searchQuery && watchlist.length < 10 && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">Quick add popular funds:</p>
                <div className="flex flex-wrap gap-2">
                  {sampleFunds
                    .filter(f => !watchlist.find(w => w.scheme_code === f.schemeCode))
                    .slice(0, 3)
                    .map((fund, index) => (
                      <Button
                        key={fund.schemeCode}
                        variant="outline"
                        size="sm"
                        className="border-white/10"
                        onClick={() => addToWatchlist(fund)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {fund.schemeName.split(' ').slice(0, 3).join(' ')}...
                      </Button>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Watchlist */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Watching ({watchlist.length} funds)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground mt-4">Loading watchlist...</p>
              </div>
            ) : watchlist.length > 0 ? (
              <div className="space-y-4">
                {watchlist.map((item, index) => (
                  <div
                    key={item.scheme_code || index}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl"
                    data-testid={`watchlist-item-${index}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <Star className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <Link
                            to={`/research/${item.scheme_code}`}
                            className="font-medium hover:text-primary flex items-center gap-1"
                          >
                            {item.scheme_name}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {item.category || "Equity"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Code: {item.scheme_code}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Current NAV</p>
                        <p className="font-mono text-lg font-semibold text-primary">
                          {item.current_nav ? formatCurrency(item.current_nav) : "Loading..."}
                        </p>
                        {item.nav_date && (
                          <p className="text-xs text-muted-foreground">{item.nav_date}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                        >
                          <Link to={`/research/${item.scheme_code}`}>
                            <Eye className="w-4 h-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromWatchlist(item.scheme_code)}
                          data-testid={`remove-watchlist-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Your Watchlist is Empty</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Add funds you want to track and monitor their NAV
                </p>
                <Button variant="outline" className="border-white/10" asChild>
                  <Link to="/research">Browse Funds</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
